import type { Header, RowData } from '@tanstack/react-table';
import classNames from 'classnames';

import CaretDown from '@/assets/icons/caret_down_fill.svg?component';
import CaretUp from '@/assets/icons/caret_up_fill.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import { ColumnTemplate } from './ColumnTemplate';
import type { DataTableFeatures, DataTableInstance } from './features';

import styles from './DataTable.module.scss';

const ARIA_SORT: Record<'asc' | 'desc', 'ascending' | 'descending'> = {
    asc: 'ascending',
    desc: 'descending',
};

function SortIndicator(props: { sorted: false | 'asc' | 'desc' }) {
    const { sorted } = props;
    if (!sorted) return <span className={styles.sortPlaceholder} />;
    return (
        <SvgIcon
            icon={sorted === 'asc' ? CaretDown : CaretUp}
            size="inherit"
            fixedWidth
            svgProps={{ 'aria-hidden': true }}
        />
    );
}

interface HeaderCellProps<T extends RowData> {
    header: Header<DataTableFeatures, T>;
    sortable: boolean;
    grid: boolean;
    /** Header rows the cell spans, for an ungrouped column under a grouped header. */
    rowSpan?: number;
    /** A group's header, which spans the columns under it. */
    isGroup: boolean;
}

function HeaderCell<T extends RowData>(props: HeaderCellProps<T>) {
    const { header, sortable, grid, rowSpan, isGroup } = props;
    const { column } = header;
    const meta = column.columnDef.meta;
    const align = meta?.align ?? 'left';
    const screenReaderText = meta?.headerScreenReaderText;
    const content = (
        <>
            <ColumnTemplate template={column.columnDef.header} context={header.getContext()} />
            {screenReaderText ? (
                <span className={styles.visuallyHidden}>{screenReaderText}</span>
            ) : null}
        </>
    );

    if (!sortable || isGroup) {
        return (
            <th
                scope={isGroup ? 'colgroup' : 'col'}
                colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                rowSpan={rowSpan}
                data-name={column.id}
                data-align={align}
                title={meta?.headerTitle}
                aria-describedby={meta?.headerDescribedBy}
                className={meta?.headerClassName}>
                {/* Grid header cells are flex boxes; the span keeps the label one item. */}
                {grid ? <span>{content}</span> : content}
            </th>
        );
    }

    const sorted = column.getIsSorted();
    const canSort = column.getCanSort();
    return (
        <th
            scope="col"
            rowSpan={rowSpan}
            data-name={column.id}
            data-align={align}
            title={meta?.headerTitle}
            aria-describedby={meta?.headerDescribedBy}
            className={classNames(canSort && styles.sortableHeader, meta?.headerClassName)}
            aria-sort={sorted ? ARIA_SORT[sorted] : undefined}
            onClick={column.getToggleSortingHandler()}>
            <span className={styles.headerLabel}>
                {' '}
                {/* The keyboard's focus target. Enter and Space click it and the click bubbles
                    to the cell's handler, so the mouse and the keyboard sort once each. A column
                    that cannot sort, such as an actions column, gets no button. */}
                {canSort ? (
                    <button type="button" className={styles.sortButton}>
                        {content}
                        <SortIndicator sorted={sorted} />
                    </button>
                ) : (
                    <>
                        {content}
                        <SortIndicator sorted={sorted} />
                    </>
                )}
            </span>
        </th>
    );
}

interface DataTableHeaderProps<T extends RowData> {
    table: DataTableInstance<T>;
    /** Sortable tables keep room for the sort arrow beside every header. */
    sortable: boolean;
    grid: boolean;
    className?: string;
}

/**
 * The header rows. With grouped columns there is a row per level: a group's header spans its
 * columns, and a column outside any group spans every row from the top.
 */
export default function DataTableHeader<T extends RowData>(props: DataTableHeaderProps<T>) {
    const { table, sortable, grid, className } = props;
    const headerGroups = table.getHeaderGroups();
    const depth = headerGroups.length;

    return (
        <thead className={className}>
            {headerGroups.map((headerGroup, rowIndex) => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const isLeaf = header.column.columns.length === 0;
                        const ungroupedLeaf = isLeaf && header.column.parent === undefined;
                        // An ungrouped column is drawn once, from its placeholder in the top
                        // row, spanning every header row.
                        if (depth > 1 && ungroupedLeaf && rowIndex > 0) return null;
                        return (
                            <HeaderCell
                                key={header.id}
                                header={header}
                                sortable={sortable}
                                grid={grid}
                                isGroup={!isLeaf}
                                rowSpan={depth > 1 && ungroupedLeaf ? depth : undefined}
                            />
                        );
                    })}
                </tr>
            ))}
        </thead>
    );
}
